import React, { useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers";
import * as dayjs from "dayjs";
import IntlMessages from "util/IntlMessages";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from "@material-ui/core";

import FormInput from "components/Form/Controls/input";
import FormDatePicker from "components/Form/Controls/datepicker";
import ButtonAsync from "components/Form/Controls/ButtonAsync";

const Form = ({
  rowObj,
  prescriptionId,
  openCRU,
  handleRequestClose,
  handleCreateUpdate,
  readOnly,
}) => {
  const defaultValues = {
    startCycleDate: null,
    endCycleDate: null,
    adverseEvent: "",
    vialsNumber: "",
    lote: "",
    expirationDate: null,
    matricule: "",
  };

  const validationSchema = yup.object().shape({
    startCycleDate: yup.date().nullable(),
    endCycleDate: yup.date().nullable(),
    adverseEvent: yup
      .string()
      .required(<IntlMessages id="cycleVials.validation.adverseEvent" />)
      .max(500, <IntlMessages id="core.max500" />),
    vialsNumber: yup
      .number()
      .typeError(<IntlMessages id="core.numberOnly" />)
      .integer(<IntlMessages id="core.integerOnly" />)
      .nullable(),
    lote: yup
      .string()
      .required(<IntlMessages id="cycleVials.validation.lote" />)
      .max(500, <IntlMessages id="core.max500" />),
    expirationDate: yup.date().nullable(),
    matricule: yup
      .string()
      .required(<IntlMessages id="cycleVials.validation.matricule" />)
      .max(500, <IntlMessages id="core.max500" />),
  });

  const methods = useForm({
    defaultValues,
    resolver: yupResolver(validationSchema),
  });

  const { handleSubmit, setValue, errors } = methods;

  useEffect(() => {
    if (openCRU) {
      setTimeout(() => loadData(rowObj), 150);
    }
  }, [openCRU]);

  const loadData = (row) => {
    if (!row) return;
    setValue("startCycleDate", row.startCycleDate ? dayjs(row.startCycleDate) : null);
    setValue("endCycleDate", row.endCycleDate ? dayjs(row.endCycleDate) : null);
    setValue("adverseEvent", row.adverseEvent ?? "");
    setValue("vialsNumber", row.vialsNumber ?? "");
    setValue("lote", row.lote ?? "");
    setValue("expirationDate", row.expirationDate ? dayjs(row.expirationDate) : null);
    setValue("matricule", row.matricule ?? "");
  };

  const formatDateOrNull = (value) =>
    value && dayjs(value).isValid()
      ? dayjs(value).format("YYYY-MM-DDTHH:mm:ss")
      : null;

  const onSubmit = async (d) => {
    const obj = {
      ...rowObj,
      id: rowObj?.id ?? 0,
      prescriptionId: prescriptionId,
      startCycleDate: formatDateOrNull(d.startCycleDate),
      endCycleDate: formatDateOrNull(d.endCycleDate),
      adverseEvent: d.adverseEvent,
      vialsNumber: d.vialsNumber ? parseInt(d.vialsNumber) : null,
      lote: d.lote,
      expirationDate: formatDateOrNull(d.expirationDate),
      matricule: d.matricule,
    };
    await handleCreateUpdate(obj);
  };

  const disabled = !!readOnly;

  return (
    <Dialog fullWidth maxWidth="md" open={openCRU} onClose={handleRequestClose}>
      <DialogTitle>
        <IntlMessages id={!rowObj ? "core.add" : "core.edit"} />
      </DialogTitle>
      <DialogContent>
        <FormProvider {...methods}>
          <form noValidate autoComplete="off">
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormDatePicker
                  name="startCycleDate"
                  label={<IntlMessages id="cycleVials.startCycleDate" />}
                  format="YYYY-MM-DD"
                  disabled={disabled}
                  errorobj={errors}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormDatePicker
                  name="endCycleDate"
                  label={<IntlMessages id="cycleVials.endCycleDate" />}
                  format="YYYY-MM-DD"
                  disabled={disabled}
                  errorobj={errors}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <FormInput
                  name="adverseEvent"
                  label={<IntlMessages id="cycleVials.adverseEvent" />}
                  disabled={disabled}
                  errorobj={errors}
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormInput
                  name="vialsNumber"
                  label={<IntlMessages id="cycleVials.vialsNumber" />}
                  disabled={disabled}
                  errorobj={errors}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormInput
                  name="lote"
                  label={<IntlMessages id="cycleVials.lote" />}
                  disabled={disabled}
                  errorobj={errors}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormDatePicker
                  name="expirationDate"
                  label={<IntlMessages id="cycleVials.expirationDate" />}
                  format="YYYY-MM-DD"
                  disabled={disabled}
                  errorobj={errors}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormInput
                  name="matricule"
                  label={<IntlMessages id="cycleVials.matricule" />}
                  disabled={disabled}
                  errorobj={errors}
                />
              </Grid>
            </Grid>
          </form>
        </FormProvider>
        {!disabled && (
          <div className="text-center mt-4">
            <ButtonAsync
              onClick={handleSubmit(onSubmit)}
              variant="contained"
              color="primary"
            >
              <IntlMessages id="core.save" />
            </ButtonAsync>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleRequestClose} color="secondary">
          <IntlMessages id="core.close" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default Form;
